import type { ReactNode } from 'react';
import { amountInWords } from '../../utils/words';
import { money } from '../../utils/format';

/**
 * The shared shape of every document this shop hands over: invoice, old gold
 * receipt, exchange acknowledgement.
 *
 * It prints from the browser rather than being rendered to PDF on the server.
 * That is a deliberate trade: a shop needs paper today, from whatever printer
 * is on the counter, and browser printing gets there with no new dependency and
 * nothing to queue or go wrong. Server-side PDF earns its place when documents
 * need emailing or archiving rather than handing across a counter.
 */

export interface PrintParty {
  name: string;
  phone?: string | null;
  gstin?: string | null;
  pan?: string | null;
  address?: string | null;
}

export interface PrintHeader {
  companyName: string;
  logoDataUri?: string | null;
  companyGstin?: string | null;
  companyPan?: string | null;
  companyAddress?: string | null;
  companyPhone?: string | null;
  companyEmail?: string | null;
  tagline?: string | null;
  branchName: string;
  branchAddress?: string | null;
  branchPhone?: string | null;
}

/** A label and its value, for the details box beside the party. */
export interface PrintDetail {
  label: string;
  value: string;
}

/**
 * The letterhead every printed sheet starts with: the shop on the left, what
 * the paper is on the right. Shared by the bill-shaped documents and the
 * melting slip, so every sheet the shop hands over looks like the same shop.
 */
export function PrintLetterhead({
  header,
  title,
  docNo,
  docDate,
  status,
}: {
  header: PrintHeader;
  title: string;
  docNo: string;
  docDate?: string;
  status?: string;
}) {
  const contact = [
    header.branchPhone || header.companyPhone ? `Ph ${header.branchPhone ?? header.companyPhone}` : null,
    header.companyEmail,
  ].filter(Boolean);
  const tax = [
    header.companyGstin ? `GSTIN ${header.companyGstin}` : null,
    header.companyPan ? `PAN ${header.companyPan}` : null,
  ].filter(Boolean);

  return (
    <div className="mb-5 flex items-start justify-between gap-6 border-b-2 border-gold-600 pb-4">
      <div className="flex min-w-0 items-start gap-3">
        {header.logoDataUri ? (
          <img src={header.logoDataUri} alt="" className="h-14 w-14 shrink-0 object-contain" />
        ) : null}
        <div className="min-w-0">
          <h1 className="text-xl font-bold leading-tight">{header.companyName}</h1>
          {header.tagline ? <p className="text-xs italic text-ink-soft">{header.tagline}</p> : null}
          <p className="text-sm">{header.branchName}</p>
          {header.branchAddress ? <p className="max-w-sm text-xs text-ink-soft">{header.branchAddress}</p> : null}
          {contact.length ? <p className="num text-xs text-ink-soft">{contact.join('  ·  ')}</p> : null}
          {tax.length ? <p className="num text-xs font-medium">{tax.join('  ·  ')}</p> : null}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm font-bold uppercase tracking-wider">{title}</p>
        <table className="ml-auto mt-1 text-xs">
          <tbody>
            <tr>
              <td className="pr-2 text-ink-soft">No.</td>
              <td className="num text-right text-sm font-semibold">{docNo}</td>
            </tr>
            {docDate ? (
              <tr>
                <td className="pr-2 text-ink-soft">Date</td>
                <td className="num text-right">{docDate}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
        {status ? <p className="mt-1 text-xs font-medium">{status}</p> : null}
      </div>
    </div>
  );
}

/**
 * The small line beneath an item saying how its price was reached. Each
 * figure stays whole — "Making ₹8,190.00" never splits across two lines.
 */
export function Breakup({ parts }: { parts: string[] }) {
  if (parts.length === 0) return null;
  return (
    <span className="breakup">
      {parts.map((p, i) => (
        <span key={i}>
          {/* The space between figures is where the line may wrap; a figure
              itself never splits. */}
          {i > 0 ? <span className="px-1 text-ink-faint">· </span> : null}
          <span className="whitespace-nowrap">{p}</span>{' '}
        </span>
      ))}
    </span>
  );
}

/** Two signature lines at the foot of a sheet. */
export function PrintSignatures({ left, right }: { left: string; right: string }) {
  return (
    <div className="keep-together mt-12 flex justify-between gap-8 text-xs">
      <div className="w-48">
        <div className="mb-1 h-10 border-b border-ink/50" />
        {left}
      </div>
      <div className="w-48 text-right">
        <div className="mb-1 h-10 border-b border-ink/50" />
        {right}
      </div>
    </div>
  );
}

export function PrintDocument({
  title,
  header,
  docNo,
  docDate,
  party,
  partyLabel,
  details,
  children,
  totals,
  grandTotal,
  grandTotalLabel = 'Total',
  paymentMethod,
  paymentLabel = 'Paid by',
  footNote,
  signatures,
}: {
  title: string;
  header: PrintHeader;
  docNo: string;
  docDate: string;
  party: PrintParty | null;
  partyLabel: string;
  /** Facts about the document itself — place of supply, ID seen, how tested. */
  details?: PrintDetail[];
  children: ReactNode;
  totals: { label: string; value: string; strong?: boolean }[];
  grandTotal: string;
  grandTotalLabel?: string;
  paymentMethod?: string;
  paymentLabel?: string;
  footNote?: string;
  signatures?: [string, string];
}) {
  const shown = (details ?? []).filter((d) => d.value && d.value.trim());
  const [signLeft, signRight] = signatures ?? ['Customer signature', `For ${header.companyName}`];

  return (
    <div className="print-sheet">
      <PrintLetterhead header={header} title={title} docNo={docNo} docDate={docDate} />

      <div className={`mb-5 grid gap-6 ${shown.length ? 'grid-cols-[minmax(0,1fr)_minmax(0,1fr)]' : ''}`}>
        <div className="min-w-0">
          <p className="doc-label">{partyLabel}</p>
          <p className="mt-0.5 text-base font-semibold leading-tight">{party?.name ?? 'Walk-in customer'}</p>
          {party?.phone ? <p className="num text-xs">Ph {party.phone}</p> : null}
          {party?.address ? <p className="text-xs text-ink-soft">{party.address}</p> : null}
          {party?.gstin || party?.pan ? (
            <p className="num text-xs">
              {[party.gstin ? `GSTIN ${party.gstin}` : null, party.pan ? `PAN ${party.pan}` : null]
                .filter(Boolean)
                .join('  ·  ')}
            </p>
          ) : null}
        </div>
        {shown.length ? (
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] content-start gap-x-4 gap-y-1 rounded border border-ink/15 px-3 py-2 text-xs">
            {shown.map((d) => (
              <div key={d.label} className="contents">
                <dt className="text-ink-soft">{d.label}</dt>
                <dd className="text-right font-medium">{d.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>

      {children}

      <div className="keep-together">
        <div className="mt-4 flex justify-end">
          <table className="w-80 text-sm">
            <tbody>
              {totals.map((t) => (
                <tr key={t.label}>
                  <td className="py-0.5 pr-4">{t.label}</td>
                  <td className={`num whitespace-nowrap py-0.5 text-right ${t.strong ? 'font-semibold' : ''}`}>
                    {t.value}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-ink">
                <td className="pt-1.5 font-semibold">{grandTotalLabel}</td>
                <td className="num whitespace-nowrap pt-1.5 text-right text-lg font-bold">{money(grandTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* The line a customer actually reads to check the figure. */}
        <p className="mt-3 border-y border-ink/10 py-1.5 text-sm">
          <span className="text-ink-soft">Amount in words: </span>
          <span className="font-medium">{amountInWords(grandTotal)}</span>
        </p>

        {paymentMethod ? (
          <p className="mt-1.5 text-sm">
            <span className="text-ink-soft">{paymentLabel}: </span>
            {paymentMethod}
          </p>
        ) : null}

        {footNote ? (
          <p className="mt-4 whitespace-pre-line text-[11px] leading-relaxed text-ink-soft">{footNote}</p>
        ) : null}

        <PrintSignatures left={signLeft} right={signRight} />
      </div>
    </div>
  );
}

/**
 * Print, or save the same sheet as a PDF.
 *
 * Both go through the browser's print dialog, where "Save as PDF" is a
 * destination — which is how you get a PDF with real, searchable text rather
 * than a photograph of a page. The document's title is set to the bill number
 * first, because that is what the browser uses as the file name: a customer
 * receives INV-000123.pdf rather than localhost.pdf.
 */
export function PrintToolbar({ onBack, fileName }: { onBack: () => void; fileName?: string }) {
  const print = (): void => {
    const was = document.title;
    if (fileName) document.title = fileName.replace(/[\\/:*?"<>|]/g, '-');
    window.print();
    // Restored after the dialog closes; printing is synchronous in every
    // browser that matters, but the timeout covers the ones that defer it.
    window.setTimeout(() => {
      document.title = was;
    }, 1000);
  };

  return (
    <div className="no-print mb-4 flex flex-wrap items-center gap-2">
      <button className="btn-primary" onClick={print}>
        Download PDF
      </button>
      <button className="btn-ghost" onClick={print}>
        Print
      </button>
      <button className="btn-quiet" onClick={onBack}>
        Back
      </button>
      <span className="text-2xs text-ink-soft">Choose “Save as PDF” as the destination to keep a copy.</span>
    </div>
  );
}
